/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Link } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useIntl } from 'bundles/page/lib/useIntl';
import KeyInformationDetailsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDetailsItem';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import {
  COURSE_NAV_ITEMS,
  SPECIALIZATION_NAV_ITEMS,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';

import _t from 'i18n!nls/xdp';

export const CONTENT_COUNT_TRACKING_NAME = 'kim_view_syllabus';
export const CONTENT_COUNT_BUTTON_TRACKING_NAME = 'description_page_view_syllabus';
export const CONTENT_COUNT_SECTION_NAME = 'kim';

type Props = {
  subtitle?: string;
  courseCount?: number;
  moduleCount?: number;
};

const styles = {
  link: css`
    /* override cds link color */
    color: var(--cds-color-neutral-primary) !important;
  `,
  detailItemWrapper: css`
    display: inline-flex;
  `,
};

export const KeyInformationContentCount = ({ subtitle, courseCount, moduleCount }: Props) => {
  const intl = useIntl();
  const eventingProductData = useEventingProductData();

  if (!courseCount && !moduleCount) {
    return null;
  }

  const getTitle = () => {
    let contentTitle;

    if (moduleCount && !contentTitle) {
      contentTitle = intl.formatMessage(_t('{moduleCount} {moduleCount, plural, =1 {module} other {modules}}'), {
        moduleCount,
      });
    }

    if (courseCount && !contentTitle) {
      contentTitle = intl.formatMessage(
        _t('#{courseCount} course series', {
          courseCount,
        })
      );
    }

    return contentTitle;
  };

  const getHref = () => {
    let href;

    if (moduleCount && !href) {
      href = COURSE_NAV_ITEMS.MODULES;
    }

    if (courseCount && !href) {
      href = SPECIALIZATION_NAV_ITEMS.COURSES;
    }

    return href;
  };

  const getSubtitle = () => {
    let contentSubtitle = subtitle;

    if (moduleCount && !contentSubtitle) {
      contentSubtitle = _t('Gain insight into a topic and learn the fundamentals.');
    }

    if (courseCount && !contentSubtitle) {
      contentSubtitle = _t('Earn a career credential that demonstrates your expertise');
    }

    return contentSubtitle;
  };

  return (
    <KeyInformationDetailsItem
      title={
        <TrackedDiv trackingName="kim_view_syllabus">
          <Link
            href={`#${getHref()}`}
            trackingData={{
              button: { name: CONTENT_COUNT_BUTTON_TRACKING_NAME },
              pageSection: {
                sectionName: CONTENT_COUNT_SECTION_NAME,
              },
              ...eventingProductData,
            }}
            noViewTracking
            css={styles.link}
          >
            {getTitle()}
          </Link>
        </TrackedDiv>
      }
      subtitle={getSubtitle()}
    />
  );
};

export default KeyInformationContentCount;
